'use client';

import { type CSSProperties, type MouseEvent } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models';
import { CreatorInfo } from '@ps/types/creator';
import { ArrowLeft } from 'lucide-react';

import { SubscribeRouteContent } from './SubscribeRouteContent';
import { useIsSubscribeBlocked } from './useIsSubscribeBlocked';
import { useSubscribeCheckoutHandoff } from './useSubscribeCheckoutHandoff';

interface Props {
  bioContext: BioContext;
  creatorInfo: CreatorInfo;
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  minLevel?: PopStoreExtendedSubscriptionLevel;
  successRedirectUrl?: string;
  /** Creator bio home — where Back lands. */
  bioHref: string;
  creatorName: string;
  /** Creator backdrop (image / gradient / color) painted behind the centered card. */
  pageBackground: CSSProperties;
}

/**
 * Standalone host for `/<url_code>/subscribe` — the cold-load / direct-link focus
 * view. NOT a dialog: it renders {@link SubscribeRouteContent} in a centered dark
 * card on the creator's bio backdrop (like the contact / tipping standalone
 * pages). Back leaves via a hard `location.replace` so a subsequent browser Back
 * skips this route (the `cacheComponents` `<Activity>` terminal-nav fix).
 */
export function SubscribeStandalone({
  bioContext,
  creatorInfo,
  defaultLevel,
  minLevel,
  successRedirectUrl,
  bioHref,
  creatorName,
  pageBackground,
}: Props) {
  const handedOff = useSubscribeCheckoutHandoff(successRedirectUrl);
  const blocked = useIsSubscribeBlocked({
    presentation: 'standalone',
    bioContext,
    creatorInfo,
    defaultLevel,
    minLevel,
    successRedirectUrl,
  });

  const leaveToBio = () => {
    if (typeof window === 'undefined') return;
    window.location.replace(bioHref);
  };

  const onBackClick = (event: MouseEvent) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    leaveToBio();
  };

  // A CONFIRMED-anonymous viewer is being redirected to login — render nothing
  // (no backdrop, no card) so the login overlay is the only thing on screen.
  // A completed subscription hides it the same way while the buyer is routed on,
  // so the picker can't repaint over the handoff.
  if (blocked || handedOff) return null;

  return (
    <div className="relative flex min-h-dvh w-full items-center justify-center p-4">
      <div className="fixed inset-0 -z-10" style={pageBackground} aria-hidden />

      <a
        href={bioHref}
        onClick={onBackClick}
        aria-label={`Back to ${creatorName}`}
        className="fixed left-3 top-3 z-20 flex h-9 items-center gap-2 rounded-full bg-black/40 px-3 text-sm font-medium text-white transition hover:bg-black/60"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        <span className="max-w-[12rem] truncate">{creatorName}</span>
      </a>

      <div className="relative flex max-h-[90vh] w-full max-w-[420px] flex-col overflow-hidden rounded-xl border border-gray-700 bg-gray-900 text-white shadow-2xl">
        <SubscribeRouteContent
          bioContext={bioContext}
          creatorInfo={creatorInfo}
          defaultLevel={defaultLevel}
          minLevel={minLevel}
          successRedirectUrl={successRedirectUrl}
          onClose={leaveToBio}
          showCloseButton={false}
        />
      </div>
    </div>
  );
}
